import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { getActivityReport } from '@/api/sysadmin/activity'

export const Route = createFileRoute('/_auth/admin/activity')({
  loader: getActivityReport,
  component: ActivityPage,
  head: () => ({ meta: [{ title: 'Activity - Snowvault Admin' }] })
})

function ActivityPage() {
  const report = Route.useLoaderData()
  const reportAt = new Date(report.reportAtTsMs)
  const formattedTime = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  }).format(reportAt)

  return (
    <div>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Activity</h1>
        <p className="mt-1 text-sm text-muted-foreground">Aggregate usage across enabled organisations.</p>
        <p className="mt-3 text-sm">
          <span className="text-muted-foreground">Calculated at</span>{' '}
          <time dateTime={reportAt.toISOString()} className="font-medium">
            {formattedTime}
          </time>
        </p>
      </div>

      {report.rows.length === 0 ? (
        <div className="mt-6 rounded-lg border border-dashed px-6 py-12 text-center">
          <p className="text-sm font-medium">No enabled organisations to report</p>
          <p className="mt-1 text-sm text-muted-foreground">The report contains no organisation rows.</p>
        </div>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-lg border">
          <Table className="min-w-[780px]">
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead scope="col">Organisation</TableHead>
                <TableHead scope="col" className="text-right">
                  Members
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Activated
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Access 7d
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Access 30d
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Chat 7d
                </TableHead>
                <TableHead scope="col" className="text-right">
                  Chat 30d
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.orgSlug}>
                  <TableCell className="font-mono text-xs font-medium">{row.orgSlug}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.members}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.activated}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.access7d}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.access30d}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.chat7d}</TableCell>
                  <TableCell className="text-right tabular-nums">{row.chat30d}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/30 font-semibold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.members}</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.activated}</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.access7d}</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.access30d}</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.chat7d}</TableCell>
                <TableCell className="text-right tabular-nums">{report.total.chat30d}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      <div className="mt-5 space-y-1 text-sm text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">Members:</span> enabled, non-sysadmin users in enabled
          organisations.
        </p>
        <p>
          <span className="font-medium text-foreground">Activated:</span> members who have logged in at least once.
        </p>
        <p>
          <span className="font-medium text-foreground">Access:</span> latest recorded login or retained session access
          in the trailing window.
        </p>
        <p>
          <span className="font-medium text-foreground">Chat:</span> members with at least one user-authored message in
          the trailing window.
        </p>
      </div>
    </div>
  )
}
